import { Ohje } from "./Ohje";
import { puuteKuvausTeksti } from "./puutekuvausTeksti";
import { useEffect, useState } from "react";
import { naytaPvm } from "./pvm";
import { nayttonimi, selite } from "./nayttonimi";
import { api, type Kayttaja, type OmatTiedot as Tiedot, type OmatTehtavat } from "./api";
import { LinjaLinkki } from "./Linkit";
import { TILAVARI } from "./Tila";
import { KieliValinta } from "./KieliValinta";
import { t } from "./kieli";

/* Oma profiili — kirjautuneen henkilötiedot + pätevyydet, vain luku.
   Hitsaaja näkee omansa; sama endpoint kaikille. */

const tilaVari = (tila: string) =>
  // Vika: pätevyys on umpeutunut, eli henkilö ei ole pätevä. Vikaväri
  // erillään brändiaksentista — asiakkaan väri saa vaihtua, tämä ei.
  // Kolme muuta haaraa odottavat vielä tokeneita (erä 2).
  tila === "vanhentunut" ? TILAVARI.vika
  : tila === "kriittinen" ? "var(--puna-tumma)"
  : tila === "varoitus" ? TILAVARI.varoitus : TILAVARI.kunnossa;

export function OmatTiedot({ kayttaja, hitsauslokiPaalla }: { kayttaja: Kayttaja; hitsauslokiPaalla: boolean }) {
  // T534 jatko (Toni 13.10.2026): «Omat tehtävät» — mitä hoidan (236), suodatin lajin mukaan. Luetaan aina, ei vain työnjohdolle:
  // myös kenttätekijä voi ottaa puutteen. Tyhjä on nolla, ei «ei tiedossa».
  const [tehtavat, setTehtavat] = useState<OmatTehtavat | null>(null);
  const [laji, setLaji] = useState<"kaikki" | "puute" | "punch">("kaikki");
  useEffect(() => { api.omatTehtavat().then(setTehtavat).catch(() => setTehtavat({ yhteensa: 0 })); }, []);
  // 127: kenttätekijä ilman hitsaustyön työlajia ei näe hitsauslokia — syy sanotaan eikä rivi vain puutu.
  const hitsauslokiPiilossa = hitsauslokiPaalla && kayttaja.roolit.includes("kenttatekija") && !kayttaja.hitsaustyo;
  const [d, setD] = useState<Tiedot | null>(null);
  const [virhe, setVirhe] = useState(false);

  useEffect(() => { api.minun().then(setD).catch(() => setVirhe(true)); }, []);

  // Kenttätekijä, jonka yksikään työmaa ei näy, saa syyn eikä tyhjää ruutua (13.9.2026): kielto sanotaan myös hänelle.
  const kenttatekija = kayttaja.roolit.includes("kenttatekija");
  const [tyomaaSyy, setTyomaaSyy] = useState<string | null>(null);
  useEffect(() => {
    if (!kenttatekija || kayttaja.kohteet.length > 0) return;
    api.minunTyomaa().then((t) => setTyomaaSyy(t.peruste)).catch(() => setTyomaaSyy(null));
  }, [kenttatekija, kayttaja.kohteet.length]);

  return (
    <>
      <header className="head">
        <div className="eyebrow">{t("omat.oma_profiili")}</div>
        <div className="head__rivi"><h1>{t("valikko.omat_tiedot")}</h1></div>
      </header>

      {/* T439: kieli tunnukselle. Ylimpänä, koska englanninkielinen käyttäjä etsii sen ennen kuin lukee mitään muuta. */}
      <KieliValinta nykyinen={kayttaja.kieli ?? null} />

      {/* Ohje ylös (Toni 24.9.2026). Lähde: /api/minun ja /api/minun/tyomaa: vaadi, oma henkilö. Alalaidan «vain luettavissa» siirtyi tähän. */}
      <Ohje
        kuka={<>{t("omat.ohje.kuka")}</>}
        luvut={<>{t("omat.ohje.luvut")}</>} />

      {virhe && <p className="huom huom--vika">{t("omat.haku_virhe")}</p>}
      {tyomaaSyy && (
        <p className="huom" style={{ maxWidth: 560 }} data-tyomaa-syy>
          {t("omat.ei_tyomaata", { syy: tyomaaSyy })}
        </p>
      )}
      {hitsauslokiPiilossa && (
        <p className="huom" style={{ maxWidth: 560 }}>
          {t("omat.hitsausloki_piilossa")}
        </p>
      )}
      {!d && !virhe && <p className="dim">{t("yleinen.ladataan")}</p>}

      {d && !d.henkilo && (
        <p className="dim" data-sisalto="">{t("omat.ei_henkiloa")}</p>
      )}

      {d?.henkilo && (
        <section className="panel" style={{ maxWidth: 560 }} data-sisalto="">
          <div style={{ fontSize: 22, fontWeight: 800 }}>
            {d.henkilo.nimi}
            {d.henkilo.lyhenne && <span className="dim mono" style={{ fontSize: 14, marginLeft: 10 }}>{d.henkilo.lyhenne}</span>}
          </div>
          {d.henkilo.nimike && <div className="dim" style={{ marginTop: 2 }}>{d.henkilo.nimike}</div>}

          <div style={{ marginTop: 12, fontSize: 14, lineHeight: 1.8 }}>
            {d.henkilo.puhelin && <div>{t("omat.puhelin")}: <span className="mono">{d.henkilo.puhelin}</span></div>}
            {d.henkilo.sahkoposti && <div>{t("kirjautuminen.sahkoposti")}: {d.henkilo.sahkoposti}</div>}
            {d.henkilo.tyosuhde && <div className="dim">{t("omat.tyosuhde")}: {nayttonimi("tyosuhde", d.henkilo.tyosuhde)}</div>}
          </div>

          {d.henkilo.osaamiset.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div className="eyebrow" style={{ marginBottom: 6 }}>{t("omat.osaamiset")}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {d.henkilo.osaamiset.map((o) => (
                  <span key={o} style={{ background: "var(--sinkki)", border: "1px solid var(--viiva)", padding: "3px 8px", fontSize: 13 }}>{nayttonimi("osaamisluokka_nimi", o)}</span>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 18 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>{t("valikko.patevyydet")}</div>
            {d.patevyydet.length === 0 && <p className="dim" style={{ fontSize: 13 }}>{t("omat.ei_patevyyksia")}</p>}
            {d.patevyydet.map((p) => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "5px 0", borderBottom: "1px solid var(--viiva)" }}>
                <span>
                  {p.todistuksia > 0 ? "✓ " : ""}{nayttonimi("patevyystyyppi_nimi", p.patevyys)}{p.taso ? <span className="dim"> · {p.taso}</span> : null}
                </span>
                <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span className="dim mono" >{naytaPvm(p.voimassa_asti) ?? t("omat.ei_vanhene")}</span>
                  <span style={{ color: tilaVari(p.tila), fontSize: 12, fontWeight: 700 }} title={selite("patevyys_tila", p.tila)}>{nayttonimi("patevyys_tila", p.tila)}</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* T534 jatko: omat tehtävät — puutteet ja punchit joita hoidan. Rivi vie listaan, jossa hoito vapautetaan tai työ suljetaan. */}
      <section className="panel" style={{ maxWidth: 560 }} data-omat-tehtavat={tehtavat?.yhteensa ?? ""}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <h2 style={{ fontSize: 15, margin: 0 }}>{t("omat.tehtavat")}{tehtavat && ` (${tehtavat.yhteensa})`}</h2>
          <label className="dim" style={{ fontSize: 13 }}>{t("omat.tehtavat_laji")}{" "}
            <select value={laji} data-omat-tehtavat-laji="" onChange={(e) => setLaji(e.target.value as typeof laji)}>
              <option value="kaikki">{t("pu.kaikki")}</option>
              {tehtavat?.puutteet && <option value="puute">{t("omat.tehtavat_puutteet")}</option>}
              {tehtavat?.punchit && <option value="punch">{t("omat.tehtavat_punchit")}</option>}
            </select>
          </label>
        </div>
        {tehtavat === null ? <p className="dim">{t("ltar.ladataan")}</p>
          : tehtavat.yhteensa === 0 ? <p className="dim" data-omat-tehtavat-tyhja="">{t("omat.ei_tehtavia")}</p>
          : (
            <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0" }}>
              {(laji !== "punch" ? (tehtavat.puutteet ?? []) : []).map((p) => (
                <li key={`puute-${p.id}`} data-omat-tehtava={`puute-${p.id}`} style={{ padding: "6px 0", borderBottom: "1px solid var(--viiva)" }}>
                  <a href={`#/puutteet?hoitaja=minun${p.liittyva_kohde_id ? `&kohde_id=${p.liittyva_kohde_id}` : ""}`}>{puuteKuvausTeksti(p.kuvaus)}</a>
                  <div className="dim">
                    {t("omat.tehtava_puute")}{p.vaikutus === "pysayttaa" && <> · <b style={{ color: "var(--puna)" }}>{nayttonimi("puute_vaikutus", "pysayttaa")}</b></>}
                    {p.kohteen_nimi && ` · ${p.kohteen_nimi}`}{p.linja_tunnus && <> · <LinjaLinkki tunnus={p.linja_tunnus} /></>}
                    {" · "}{naytaPvm(p.otettu.slice(0, 10))}{p.osoittaja && ` · ${t("hoito.osoitti", { nimi: p.osoittaja })}`}
                  </div>
                </li>
              ))}
              {(laji !== "puute" ? (tehtavat.punchit ?? []) : []).map((p) => (
                <li key={`punch-${p.id}`} data-omat-tehtava={`punch-${p.id}`} style={{ padding: "6px 0", borderBottom: "1px solid var(--viiva)" }}>
                  <a href={`#/selailu?nayta=punchit&hoitaja=minun&kohde=${p.kohde_id}`}>{p.vika}</a>
                  <div className="dim">
                    {t("omat.tehtava_punch")}{p.luokka && ` ${p.luokka}`}{p.kohde && ` · ${p.kohde}`} · <LinjaLinkki tunnus={p.linja} />
                    {" · "}{naytaPvm(p.otettu.slice(0, 10))}{p.osoittaja && ` · ${t("hoito.osoitti", { nimi: p.osoittaja })}`}
                    {/* Toni 17.10.2026 klo 07.40 kohta 2: kuvamäärä; linkki linjan punch-listaan, jossa kuvat aukeavat pienoiskuvina. */}
                    {(p.liitteita ?? 0) > 0 && <> · <a href={`#/selailu/${encodeURIComponent(p.linja)}`} data-omat-punch-kuvat={p.id}>{t("hl.kuvat_n", { n: p.liitteita ?? 0 })}</a></>}
                  </div>
                </li>
              ))}
            </ul>
          )}
      </section>

    </>
  );
}
